import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { io } from "socket.io-client";
import { BASE_URL } from "../../api/config";
import { getConversations } from "../../api/teamChat";
import { getNotificationPrefs } from "../../api/teamChat";

const TeamPresenceContext = createContext(null);

export const useTeamPresence = () => {
  const ctx = useContext(TeamPresenceContext);
  if (!ctx) {
    // Return safe defaults if used outside provider
    return {
      onlineUsers: new Set(),
      typingUsers: {},
      unreadCounts: {},
      mutedConversations: [],
      isConnected: false,
      socket: null,
    };
  }
  return ctx;
};

export const TeamPresenceProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [mutedConversations, setMutedConversations] = useState([]);

  // Track which conversation is currently being viewed
  const activeTeamIdRef = useRef(null);

  /* ── Socket connection ─────────────────────────────────────────────── */
  useEffect(() => {
    const token = localStorage.getItem("user");
    if (!token) return;

    const url = BASE_URL.replace(/\/api\/.*$/, "");

    const socket = io(url, {
      query: { token },
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = socket;

    // Dedupe set to prevent double-toasting when both new_message and
    // share_received fire for the same message (user already in room)
    const toastedMessageIds = new Set();

    socket.on("connect", () => {
      console.log("[TeamPresence] Connected:", socket.id);
      setIsConnected(true);
    });

    socket.on("disconnect", (reason) => {
      console.log("[TeamPresence] Disconnected:", reason);
      setIsConnected(false);
    });

    socket.on("connect_error", (err) => {
      console.error("[TeamPresence] Connection error:", err.message);
      setIsConnected(false);
    });

    /* ── Presence ── */
    socket.on("presence_sync", (data) => {
      setOnlineUsers(new Set(data.online_uuids || []));
    });

    socket.on("presence", ({ uuid, status }) => {
      setOnlineUsers((prev) => {
        const next = new Set(prev);
        if (status === "online") next.add(uuid);
        else next.delete(uuid);
        return next;
      });
    });

    /* ── Typing ── */
    socket.on("user_typing", ({ uuid, name, team_id }) => {
      setTypingUsers((prev) => ({
        ...prev,
        [team_id]: { ...(prev[team_id] || {}), [uuid]: name },
      }));
    });

    socket.on("user_stop_typing", ({ uuid, team_id }) => {
      setTypingUsers((prev) => {
        const room = { ...(prev[team_id] || {}) };
        delete room[uuid];
        const next = { ...prev };
        if (Object.keys(room).length === 0) delete next[team_id];
        else next[team_id] = room;
        return next;
      });
    });

    /* ── New messages → unread count ── */
    socket.on("new_message", (msg) => {
      console.log("[NEW_MESSAGE] fired:", msg.type, msg.team_id); // ← add this
      if (msg.team_id !== activeTeamIdRef.current) {
        setUnreadCounts((prev) => ({
          ...prev,
          [msg.team_id]: (prev[msg.team_id] || 0) + 1,
        }));

        // Fire toast (TeamToastContainer will check muted list)
        window.dispatchEvent(
          new CustomEvent("team:toast-message", { detail: msg }),
        );
        toastedMessageIds.add(msg.message_id);
      }

      window.dispatchEvent(
        new CustomEvent("team:new-message", { detail: msg }),
      );
    });

    /* ── Read receipts ── */
    socket.on("read_receipt", (data) => {
      window.dispatchEvent(
        new CustomEvent("team:read-receipt", { detail: data }),
      );
    });

    /* ── Invited to conversation ── */
    socket.on("invited_to_conversation", (data) => {
      socket.emit("join_conversation", { team_id: data.team_id });
      window.dispatchEvent(new CustomEvent("team:invited", { detail: data }));
      // Refresh sidebar
      window.dispatchEvent(
        new CustomEvent("sidebar:refresh-team-conversations"),
      );
    });

    /* ── Share received (direct notification for users not in room) ── */
    socket.on("share_received", (data) => {
      console.log("[SHARE_RECEIVED] fired:", data); // ← add this
      // Join the room so future messages come through normally
      socket.emit("join_conversation", { team_id: data.team_id });

      // Fire toast only if new_message didn't already toast this message
      if (
        data.team_id !== activeTeamIdRef.current &&
        !toastedMessageIds.has(data.message?.message_id)
      ) {
        window.dispatchEvent(
          new CustomEvent("team:toast-message", { detail: data.message }),
        );
        toastedMessageIds.add(data.message?.message_id);
      }

      // Refresh shared-with-me sidebar sections
      window.dispatchEvent(new Event("sidebar:refresh-shared"));

      // Refresh conversation list in case this is a conversation they haven't seen
      window.dispatchEvent(
        new CustomEvent("sidebar:refresh-team-conversations"),
      );
    });

    socket.on("test_ping", (data) =>
      console.log("[TEST] room-based ping:", data),
    );
    socket.on("test_ping_broadcast", (data) =>
      console.log("[TEST] broadcast ping:", data),
    );

    /* ── Errors ── */
    socket.on("error", (data) => {
      console.error("[TeamPresence] Server error:", data.message);
    });

    /* ── Join room on demand (newly created conversations) ── */
    const joinHandler = (e) => {
      const teamId = e.detail?.team_id;
      if (teamId) socket.emit("join_conversation", { team_id: teamId });
    };
    window.addEventListener("team:join-room", joinHandler);

    /* ── Invite members to join (newly created DMs/groups) ── */
    const inviteHandler = (e) => {
      const { team_id, members } = e.detail || {};
      if (team_id && members?.length) {
        socket.emit("invite_to_room", { team_id, member_uuids: members });
      }
    };
    window.addEventListener("team:invite-members", inviteHandler);

    /* ── Message edited ── */
    socket.on("message_edited", (data) => {
      window.dispatchEvent(
        new CustomEvent("team:message-edited", { detail: data }),
      );
    });

    /* ── Message deleted ── */
    socket.on("message_deleted", (data) => {
      window.dispatchEvent(
        new CustomEvent("team:message-deleted", { detail: data }),
      );
    });

    /* ── Emoji reaction ── */
    socket.on("message_reacted", (data) => {
      window.dispatchEvent(
        new CustomEvent("team:message-reacted", { detail: data }),
      );
    });

    /* ── Message pinned/unpinned ── */
    socket.on("message_pinned", (data) => {
      window.dispatchEvent(
        new CustomEvent("team:message-pinned", { detail: data }),
      );
    });

    /* ── AI streaming ── */
    socket.on("ai_stream_start", (data) => {
      window.dispatchEvent(
        new CustomEvent("team:ai-stream-start", { detail: data }),
      );
    });

    socket.on("ai_stream_chunk", (data) => {
      window.dispatchEvent(
        new CustomEvent("team:ai-stream-chunk", { detail: data }),
      );
    });

    socket.on("ai_stream_end", (data) => {
      window.dispatchEvent(
        new CustomEvent("team:ai-stream-end", { detail: data }),
      );
    });

    return () => {
      window.removeEventListener("team:join-room", joinHandler);
      window.removeEventListener("team:invite-members", inviteHandler);
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  /* ── Load muted conversations on mount ─────────────────────────────── */
  useEffect(() => {
    getNotificationPrefs()
      .then((res) => {
        setMutedConversations(res?.data?.muted_conversations || []);
      })
      .catch((e) => console.error("Failed to load notification prefs:", e));
  }, []);

  /* ── Mark conversation as active (clears unread) ───────────────────── */
  const setActiveConversation = useCallback((teamId) => {
    activeTeamIdRef.current = teamId;
    if (teamId) {
      setUnreadCounts((prev) => {
        if (!prev[teamId]) return prev;
        const next = { ...prev };
        delete next[teamId];
        return next;
      });
    }
  }, []);

  /* ── Emit helpers ──────────────────────────────────────────────────── */
  const sendMessage = useCallback(
    (team_id, content, mentions = [], reply_to = null) => {
      const payload = { team_id, content, mentions };
      if (reply_to) payload.reply_to = reply_to;
      socketRef.current?.emit("send_message", payload);
    },
    [],
  );
  const emitTyping = useCallback((team_id) => {
    socketRef.current?.emit("typing", { team_id });
  }, []);

  const emitStopTyping = useCallback((team_id) => {
    socketRef.current?.emit("stop_typing", { team_id });
  }, []);

  const markRead = useCallback((team_id, message_ids) => {
    if (message_ids.length > 0) {
      socketRef.current?.emit("mark_read", { team_id, message_ids });
    }
  }, []);

  const editMessage = useCallback((team_id, message_id, content) => {
    socketRef.current?.emit("edit_message", { team_id, message_id, content });
  }, []);

  const deleteMessage = useCallback((team_id, message_id) => {
    socketRef.current?.emit("delete_message", { team_id, message_id });
  }, []);

  const joinConversation = useCallback((team_id) => {
    socketRef.current?.emit("join_conversation", { team_id });
  }, []);

  const leaveConversation = useCallback((team_id) => {
    socketRef.current?.emit("leave_conversation", { team_id });
  }, []);

  const inviteToRoom = useCallback((team_id, member_uuids) => {
    socketRef.current?.emit("invite_to_room", { team_id, member_uuids });
  }, []);

  const reactToMessage = useCallback((team_id, message_id, emoji) => {
    socketRef.current?.emit("react_message", { team_id, message_id, emoji });
  }, []);

  const pinMessage = useCallback((team_id, message_id, pinned) => {
    socketRef.current?.emit("pin_message", { team_id, message_id, pinned });
  }, []);

  const updateMutedConversation = useCallback((teamId, muted) => {
    setMutedConversations((prev) =>
      muted
        ? [...prev.filter((id) => id !== teamId), teamId]
        : prev.filter((id) => id !== teamId),
    );
  }, []);

  const value = {
    socket: socketRef,
    isConnected,
    onlineUsers,
    typingUsers,
    unreadCounts,
    setActiveConversation,
    sendMessage,
    emitTyping,
    emitStopTyping,
    markRead,
    editMessage,
    deleteMessage,
    joinConversation,
    leaveConversation,
    inviteToRoom,
    reactToMessage,
    pinMessage,
    mutedConversations,
    updateMutedConversation,
  };

  return (
    <TeamPresenceContext.Provider value={value}>
      {children}
    </TeamPresenceContext.Provider>
  );
};

export default TeamPresenceContext;
